/**
 * Vakuai Database Explorer (product_admin only).
 *
 * A read-only browser over every Mongo collection in the system. The sidebar
 * lists collections grouped by domain (ERP, Telecom, Treasury, Admin) with
 * row counts; clicking a collection opens a paginated row browser with a
 * tenant filter (product_admin only) and a free-text search box. Rows are
 * shown as truncated JSON cards with a "View full row" expander.
 */
import React, { useCallback, useEffect, useMemo, useState } from "react";
import api from "@/lib/api";
import { PageHeader } from "@/components/layout/PageHeader";
import {
  Database, CaretRight, MagnifyingGlass, ArrowsCounterClockwise,
  CaretLeft, CaretDoubleRight, CaretDoubleLeft, Stack, X,
} from "@phosphor-icons/react";

const GROUP_ORDER = ["ERP Agents", "Finance Efficiency", "Telecom Solution", "Treasury", "Admin", "Other"];

const RowJson = ({ row }) => {
  const [open, setOpen] = useState(false);
  const preview = useMemo(() => {
    const keys = Object.keys(row).slice(0, 5);
    return keys.map((k) => `${k}: ${String(row[k]).slice(0, 60)}`).join("  ·  ");
  }, [row]);
  return (
    <div className="border border-gray-200 mb-2">
      <button onClick={() => setOpen((o) => !o)}
        className="w-full text-left px-3 py-2 hover:bg-gray-50 flex items-center justify-between gap-3"
        data-testid="row-preview">
        <span className="text-[11px] font-mono text-gray-700 truncate">{preview}</span>
        <CaretRight size={12} weight="bold" className={`shrink-0 transition-transform ${open ? "rotate-90" : ""}`} />
      </button>
      {open && (
        <pre className="text-[11px] font-mono bg-[#0B1733] text-emerald-300 p-3 overflow-x-auto whitespace-pre-wrap break-words"
          data-testid="row-json">{JSON.stringify(row, null, 2)}</pre>
      )}
    </div>
  );
};

export default function VakuaiDataExplorerPage() {
  const [collections, setCollections] = useState([]);
  const [meta, setMeta] = useState({ tenant_id: "", is_product_admin: false });
  const [tenants, setTenants] = useState([]);
  const [tenantId, setTenantId] = useState("");
  const [selected, setSelected] = useState(null);
  const [rowsData, setRowsData] = useState({ rows: [], total: 0 });
  const [schema, setSchema] = useState({});
  const [search, setSearch] = useState("");
  const [page, setPage] = useState(0);
  const PAGE = 25;
  const [loading, setLoading] = useState(true);
  const [loadingRows, setLoadingRows] = useState(false);

  const loadCollections = useCallback(async () => {
    setLoading(true);
    try {
      const params = tenantId ? { tenant_id: tenantId } : {};
      const { data } = await api.get("/vakuai-db/collections", { params });
      setCollections(data.collections || []);
      setMeta({ tenant_id: data.tenant_id, is_product_admin: data.is_product_admin });
      if (data.is_product_admin && tenants.length === 0) {
        const t = await api.get("/vakuai-db/tenants");
        setTenants(t.data.tenants || []);
      }
    } finally { setLoading(false); }
  }, [tenantId, tenants.length]);

  const loadRows = useCallback(async () => {
    if (!selected) return;
    setLoadingRows(true);
    try {
      const params = { skip: page * PAGE, limit: PAGE };
      if (tenantId) params.tenant_id = tenantId;
      if (search.trim()) params.q = search.trim();
      const { data } = await api.get(`/vakuai-db/collections/${selected}/rows`, { params });
      setRowsData({ rows: data.rows, total: data.total });
      const sch = await api.get(`/vakuai-db/collections/${selected}/schema`);
      setSchema(sch.data.fields || {});
    } finally { setLoadingRows(false); }
  }, [selected, tenantId, search, page]);

  useEffect(() => { loadCollections(); }, [loadCollections]);
  useEffect(() => { loadRows(); }, [loadRows]);

  const grouped = useMemo(() => {
    const out = {};
    for (const c of collections) (out[c.group] = out[c.group] || []).push(c);
    return out;
  }, [collections]);

  const lastPage = Math.max(0, Math.ceil(rowsData.total / PAGE) - 1);

  return (
    <div>
      <PageHeader
        eyebrow="Admin Console"
        title="Vakuai Database"
        subtitle="Every Vakuai-native collection in this tenant — execution logs, master data, treasury, ALM, and admin. Read-only with tenant-scoped filtering."
        testid="vakuai-db-title"
      />
      <div className="p-8 flex gap-6">
        {/* Sidebar: collections grouped by domain */}
        <aside className="w-72 shrink-0">
          <div className="flex items-center justify-between mb-3">
            <div className="text-[10px] tracking-[0.2em] uppercase font-bold text-gray-500">
              Collections <span className="text-gray-400">({collections.length})</span>
            </div>
            <button onClick={loadCollections} title="Refresh"
              data-testid="db-refresh"
              className="text-gray-400 hover:text-[#0020C2]">
              <ArrowsCounterClockwise size={12} weight="bold" />
            </button>
          </div>

          {meta.is_product_admin && (
            <div className="mb-4">
              <label className="text-[10px] tracking-[0.18em] uppercase font-bold text-gray-500">Tenant filter</label>
              <select
                value={tenantId}
                onChange={(e) => { setTenantId(e.target.value); setPage(0); }}
                data-testid="db-tenant-filter"
                className="mt-1 w-full border border-gray-300 px-2 py-1.5 text-xs bg-white font-mono"
              >
                <option value="">My tenant ({meta.tenant_id})</option>
                {tenants.map((t) => <option key={t} value={t}>{t}</option>)}
              </select>
            </div>
          )}

          <div className="space-y-3" data-testid="db-collection-list">
            {GROUP_ORDER.filter((g) => grouped[g]).map((g) => (
              <div key={g}>
                <div className="text-[10px] tracking-[0.2em] uppercase font-bold text-[#0020C2] mb-1 flex items-center gap-1.5">
                  <Stack size={11} weight="fill" /> {g}
                </div>
                <div className="border border-gray-200 divide-y divide-gray-100 bg-white">
                  {grouped[g].map((c) => {
                    const on = c.name === selected;
                    return (
                      <button key={c.name} onClick={() => { setSelected(c.name); setPage(0); }}
                        data-testid={`db-collection-${c.name}`}
                        className={`w-full text-left px-3 py-2 hover:bg-gray-50 ${on ? "bg-blue-50" : ""}`}>
                        <div className={`text-[12px] font-medium ${on ? "text-[#0020C2]" : "text-gray-800"}`}>{c.label}</div>
                        <div className="text-[10px] font-mono text-gray-500 flex items-center justify-between mt-0.5">
                          <span>{c.name}</span>
                          <span>
                            <span className="text-emerald-600 font-bold">{c.tenant_rows}</span>
                            <span className="text-gray-400">/{c.total_rows}</span>
                          </span>
                        </div>
                      </button>
                    );
                  })}
                </div>
              </div>
            ))}
            {loading && <div className="text-xs text-gray-500">Loading collections…</div>}
          </div>
        </aside>

        {/* Detail panel */}
        <section className="flex-1 min-w-0">
          {!selected ? (
            <div className="border border-dashed border-gray-300 p-12 text-center text-sm text-gray-500" data-testid="db-empty-state">
              <Database size={32} weight="thin" className="mx-auto mb-3 text-gray-400" />
              Select a collection from the left to browse rows.
            </div>
          ) : (
            <>
              <div className="flex items-center gap-3 mb-4">
                <div className="text-[10px] tracking-[0.2em] uppercase font-bold text-gray-500">Browsing</div>
                <div className="font-mono text-sm font-bold">{selected}</div>
                <div className="text-[11px] text-gray-500" data-testid="db-row-count">
                  {rowsData.total.toLocaleString()} rows in tenant
                </div>
                <button onClick={() => setSelected(null)} className="ml-auto text-gray-400 hover:text-gray-800"
                  data-testid="db-close-collection" title="Close"><X size={14} weight="bold" /></button>
              </div>

              <div className="flex items-center gap-3 mb-4">
                <div className="relative flex-1 max-w-md">
                  <MagnifyingGlass size={14} weight="bold" className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
                  <input
                    value={search}
                    onChange={(e) => { setSearch(e.target.value); setPage(0); }}
                    placeholder="Search any string field…"
                    data-testid="db-row-search"
                    className="w-full border border-gray-300 pl-9 pr-3 py-1.5 text-sm focus:outline-none focus:border-[#0020C2]"
                  />
                </div>
                <div className="flex items-center gap-1 text-[11px] text-gray-600">
                  <button disabled={page === 0} onClick={() => setPage(0)}
                    className="p-1 disabled:text-gray-300 hover:text-[#0020C2]" data-testid="db-first"><CaretDoubleLeft size={12} weight="bold" /></button>
                  <button disabled={page === 0} onClick={() => setPage((p) => p - 1)}
                    className="p-1 disabled:text-gray-300 hover:text-[#0020C2]" data-testid="db-prev"><CaretLeft size={12} weight="bold" /></button>
                  <span className="px-2 font-mono">{page + 1} / {lastPage + 1}</span>
                  <button disabled={page >= lastPage} onClick={() => setPage((p) => p + 1)}
                    className="p-1 disabled:text-gray-300 hover:text-[#0020C2]" data-testid="db-next"><CaretRight size={12} weight="bold" /></button>
                  <button disabled={page >= lastPage} onClick={() => setPage(lastPage)}
                    className="p-1 disabled:text-gray-300 hover:text-[#0020C2]" data-testid="db-last"><CaretDoubleRight size={12} weight="bold" /></button>
                </div>
              </div>

              {Object.keys(schema).length > 0 && (
                <div className="mb-4 border border-gray-200 bg-gray-50 p-3" data-testid="db-schema">
                  <div className="text-[10px] tracking-[0.2em] uppercase font-bold text-gray-500 mb-1.5">Schema (sampled)</div>
                  <div className="flex flex-wrap gap-1.5">
                    {Object.entries(schema).map(([k, t]) => (
                      <span key={k} className="text-[10px] font-mono px-2 py-0.5 bg-white border border-gray-200">
                        {k}<span className="text-gray-400">:{t}</span>
                      </span>
                    ))}
                  </div>
                </div>
              )}

              <div data-testid="db-rows">
                {loadingRows && <div className="text-xs text-gray-500 p-4">Loading rows…</div>}
                {!loadingRows && rowsData.rows.length === 0 && (
                  <div className="border border-dashed border-gray-300 p-8 text-center text-sm text-gray-500">
                    No rows found for this filter.
                  </div>
                )}
                {rowsData.rows.map((r, i) => <RowJson key={r.id || r._id || i} row={r} />)}
              </div>
            </>
          )}
        </section>
      </div>
    </div>
  );
}
